import { TableColumnsType } from "antd";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  BasePagination,
  CustomError,
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { ReportInventoryCameraRow } from "api/interfaces/reportInterface.interface";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { useMenu } from "hook/useMenu";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { useQuery } from "react-query";
import { GetCameraInfoResponse } from "api/interfaces/recorderInterface.interface";
import CameraPresenter from "./CameraPresenter";
import * as mediaQuery from "components/MediaQuery";
import { getCameraInfo } from "api/cameraAPI";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import { convertMacAddrFormat } from "utils/RecorderUtil";

const inititalCameraQueryInfo: BasePagination = {
  ...baseQueryInfo,
};

export default function CameraContainer() {
  const {
    setLicenseCount,
    setSubscriptionInfo,
    setTitleComponent,
    setCenterChildren,
  } = useOutletContext<any>() ?? {};
  const [searchParams, setSearchParams] = useSearchParams();

  const { mergedSystemId, recorderId } = useParams();
  const { menuNavigation } = useMenu({});
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const [cameraList, setCameraList] = useState<ReportInventoryCameraRow[]>([]);
  const isMobile = mediaQuery.useIsMobile();
  const [pageInfo, setPageInfo] = useState<Pick<BasePagination, "total">>({
    total: inititalCameraQueryInfo.total,
  });

  const baseUrl =
    selectedAccount.accountLevel === LEVEL_TYPE.EU
      ? "/recorders/list"
      : "/maintenance/staging";

  const cameraQueryInfo = useMemo<BasePagination>(() => {
    const get = (key: string) => searchParams.get(key);

    return {
      ...inititalCameraQueryInfo,
      ...pageInfo,
      ...(get("pageNum") && { pageNum: Number(get("pageNum")) }),
      ...(get("pageLimit") && { pageLimit: Number(get("pageLimit")) }),
      ...{ keyword: get("keyword") ?? "" },
      ...(get("sortType") && { sortType: get("sortType")! }),
      ...(get("sortDirection") && {
        sortDirection: get("sortDirection") as SORT_DIRECTION,
      }),
      ...(get("type") && { type: get("type") }),
    };
  }, [pageInfo, searchParams]);

  // queryInfo를 바꾸고 싶을 때는 이 함수를 통해 searchParams만 업데이트
  const updateQueryInfo = useCallback(
    (partial: Partial<BasePagination>) => {
      let shouldReplace = false;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(partial).forEach(([key, value]) => {
            if (
              key === "pageNum" &&
              prev.get(key) === null &&
              Number(value) === 0
            ) {
              next.delete(key);
              return;
            }

            if (value === null || value === undefined || value === "") {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          });
          if (next.toString() === prev.toString()) {
            return prev;
          }
          shouldReplace = true;
          return next;
        },
        { replace: shouldReplace }
      );
    },
    [setSearchParams]
  );
  const onHeaderCell = useCallback(
    (column: { key?: React.Key }) => {
      return {
        onClick: () => {
          if (!column.key) return;

          updateQueryInfo({
            sortType: column.key as string,
            sortDirection: onSortAntd(cameraQueryInfo.sortDirection),
          });
        },
      };
    },
    [cameraQueryInfo.sortDirection, cameraQueryInfo.sortType, updateQueryInfo]
  );
  const onChangePage = useCallback(
    (page: number) => {
      if (page !== cameraQueryInfo.pageNum + 1) {
        updateQueryInfo({ pageNum: page - 1 });
      }
    },
    [cameraQueryInfo.pageNum, updateQueryInfo]
  );
  const cameraQueryKey = useQueryKey(cameraQueryInfo);
  const cameraQuery = useQuery(
    [
      "recorderCamera",
      cameraQueryKey,
      mergedSystemId,
      selectedAccount.accountId,
      recorderId,
    ],
    () =>
      getCameraInfo({
        accountId: selectedAccount.accountId,
        mergedSystemId: mergedSystemId as string,
        recorderId: recorderId,
        payload: cameraQueryInfo,
      }),
    {
      enabled: !!mergedSystemId,
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (data: GetCameraInfoResponse) => {
        if (data.error !== 0 || data.result === undefined) {
          setCameraList([]);
          setPageInfo({ total: inititalCameraQueryInfo.total });
          return;
        }
        setCameraList(
          (data.result?.cameraList || []).map((camera) => ({
            ...camera,
            version:
              camera.version === "Offline" ? "N/A" : camera.version ?? "N/A",
          }))
        );
        setTitleComponent(
          [
            {
              label: "Recorder List",
              onClick: () =>
                menuNavigation({
                  to: baseUrl,
                }),
            },
            {
              label: data?.result?.mergedSystemName
                ? data?.result?.mergedSystemName
                : "",
              onClick: () =>
                menuNavigation({
                  to: `${baseUrl}/cameras/${mergedSystemId}`,
                }),
            },
            {
              label: data?.result?.recorderName
                ? `${data?.result?.recorderName}`
                : "",
              onClick: () => {},
            },
            {
              label: "Cameras",
              onClick: () => {},
            },
          ].filter((item) => item.label !== "")
        );
        setPageInfo({ total: data.page.total });
        setLicenseCount?.({
          totalCameraCount: data.page.total,
          totalLicensedCameraCount: data.page.total,
        });
        setSubscriptionInfo?.(data.result?.subscription);
      },
      onError: (_e: CustomError) => {
        setCameraList([]);
        setTitleComponent([
          {
            label: "Recorder List",
            onClick: () =>
              menuNavigation({
                to: baseUrl,
              }),
          },
          {
            label: "Cameras",
            onClick: () => {},
          },
        ]);
      },
    }
  );
  useStompDebouncedRefetch({
    selector: (state) =>
      ({
        recorderListUpdated: state.stomp.recorderListUpdated,
        subscriptionActivatedUpdated:
          state.subscriptionStomp.subscriptionActivatedUpdated,
      } as Record<string, unknown>),
    stompKey: [
      "recorderListUpdated",
      "subscriptionActivatedUpdated",
    ] as StompDateSignalKey[],
    refetch: cameraQuery.refetch,
  });

  const columns: TableColumnsType<ReportInventoryCameraRow> = [
    {
      title: "Camera",
      sorter: true,
      sortOrder: changeSortOrderAntd(cameraQueryInfo, "name"),
      onHeaderCell: onHeaderCell,
      key: "name",
      render: (value, row) => row.name,
    },
    {
      title: "Version",
      render: (value, row) => row.version || "N/A",
      width: isMobile ? "25" : "18%",
    },
    {
      title: "Manufacturer",
      render: (value, row) => row.manufacturer || "N/A",
      responsive: ["md"],
      width: "15%",
    },
    {
      title: "Model",
      render: (value, row) => row.model || "N/A",
      width: isMobile ? "25%" : "18%",
    },
    {
      title: "MAC address",
      render: (value, row) => convertMacAddrFormat(row.macAddr ?? ""),
      width: "15%",
      responsive: ["lg"],
    },
    // {
    //   title: "Storage",
    //   render: (value, row) =>
    //     formatBytesFromMB(
    //       (row.storage || []).reduce((acc, curr) => acc + curr.total, 0)
    //     ),
    //   responsive: ["md"],
    // },
    {
      title: "Resolution",
      render: (value, row) =>
        (row.streams || []).find((stream) => stream.encoderIndex === 0)
          ?.resolution || "N/A",
      responsive: ["md"],
      width: "10%",
    },
  ];

  useEffect(() => {
    setCenterChildren(null);
  }, [setCenterChildren]);

  return (
    <CameraPresenter
      cameraQueryInfo={cameraQueryInfo}
      columns={columns}
      isLoading={cameraQuery.isLoading}
      dataSource={cameraList}
      onChangePage={onChangePage}
    />
  );
}
